import { JSX, Show, createMemo, createSignal, mergeProps, splitProps } from 'solid-js';

import { Tooltip } from '../Tooltips';
import { cx } from '../../utils';

export interface CounterProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> {
  label?: JSX.Element;
  children?: JSX.Element;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  disabled?: boolean;
  formatValue?: (value: number) => JSX.Element;
  onChange?: (value: number) => void;
}

export function Counter(rawProps: CounterProps) {
  const props = mergeProps({ step: 1 }, rawProps);
  const [local, others] = splitProps(props, [
    'label',
    'children',
    'min',
    'max',
    'step',
    'value',
    'defaultValue',
    'disabled',
    'formatValue',
    'onChange',
    'class',
  ]);

  const resolvedMin = () => local.min ?? Number.NEGATIVE_INFINITY;
  const resolvedMax = () => local.max ?? Number.POSITIVE_INFINITY;
  const clamp = (val: number) => Math.min(resolvedMax(), Math.max(resolvedMin(), val));

  const [internal, setInternal] = createSignal(local.value ?? local.defaultValue ?? clamp(0));
  const currentValue = () => (local.value === undefined ? internal() : local.value);

  const isAtMin = createMemo(() => currentValue() <= resolvedMin());
  const isAtMax = createMemo(() => currentValue() >= resolvedMax());

  const setValue = (next: number) => {
    const clamped = clamp(Number(next.toFixed(4)));
    if (local.value === undefined) {
      setInternal(clamped);
    }
    local.onChange?.(clamped);
  };

  const increment = () => {
    if (local.disabled || isAtMax()) return;
    setValue(currentValue() + (local.step ?? 1));
  };

  const decrement = () => {
    if (local.disabled || isAtMin()) return;
    setValue(currentValue() - (local.step ?? 1));
  };

  const labelContent = createMemo(() => local.label ?? local.children);
  const handleValueKeyDown: JSX.EventHandlerUnion<HTMLDivElement, KeyboardEvent> = (event) => {
    if (local.disabled) return;
    if (event.key === 'ArrowUp' || event.key === '+') {
      event.preventDefault();
      increment();
    } else if (event.key === 'ArrowDown' || event.key === '-') {
      event.preventDefault();
      decrement();
    } else if (event.key === 'Home') {
      event.preventDefault();
      if (Number.isFinite(resolvedMin())) {
        setValue(resolvedMin());
      }
    } else if (event.key === 'End') {
      event.preventDefault();
      if (Number.isFinite(resolvedMax())) {
        setValue(resolvedMax());
      }
    }
  };

  const displayValue = createMemo(() => (local.formatValue ? local.formatValue(currentValue()) : currentValue()));

  return (
    <div class={cx('bz-counter', local.class, local.disabled && 'is-disabled')} {...others}>
      <div class="bz-counter__label">
        <Show when={labelContent()} fallback={<span class="bz-counter__label-placeholder" />}>
          <Tooltip content={labelContent()}>
            <div class="max-w-[50px] truncate">{labelContent()}</div>
          </Tooltip>
        </Show>
      </div>

      <div class="bz-counter__controls">
        <button
          type="button"
          class="bz-counter__button"
          onClick={decrement}
          disabled={local.disabled || isAtMin()}
          aria-label="Decrease value"
        >
          −
        </button>
        <div
          class="bz-counter__value"
          role="spinbutton"
          aria-valuemin={Number.isFinite(resolvedMin()) ? resolvedMin() : undefined}
          aria-valuemax={Number.isFinite(resolvedMax()) ? resolvedMax() : undefined}
          aria-valuenow={currentValue()}
          tabIndex={local.disabled ? -1 : 0}
          onKeyDown={handleValueKeyDown}
        >
          {displayValue()}
        </div>
        <button
          type="button"
          class="bz-counter__button"
          onClick={increment}
          disabled={local.disabled || isAtMax()}
          aria-label="Increase value"
        >
          +
        </button>
      </div>
    </div>
  );
}

export default Counter;
